import React, { useState, useEffect, useMemo, useRef } from "react";
import {
	Box,
	Typography,
	Button,
	Chip,
	Divider,
	IconButton,
	Alert,
	Snackbar,
	Tooltip,
	CircularProgress,
	FormControl,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
} from "@mui/material";
import ListPageSkeleton from "../../components/PageSkeletons/ListPageSkeleton";
import PageTitle from "../../components/PageTitle/PageTitle";
import ContextFilterAndSort from "../../components/ContextFilterAndSort/ContextFilterAndSort";
import DownloadIcon from "@mui/icons-material/Download";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import Swal from "sweetalert2";
import { useSite } from "../../context/siteContext";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Datagrid from "../../components/Datagrid/Datagrid";
import DocumentViewModal from "../../components/DocumentViewModal";
import './Documents.css'
import { useTranslation } from "react-i18next";
import { ALERT_MESSAGES, TABLE_HEADERS, UI_TEXT } from "../../constants";
import { getMachinesByIds } from "../../services/breadCrumbsService";
import { getDocumentCategories, getDocuments, getDocumentAccessUrl, exportDocumentsBulk, getDocumentsSizeEstimate } from "../../services/documentService";
import { formatMachineNumberFromRow } from "../../utilities/machineDisplay";

const Documents = () => {
	const [loading, setLoading] = useState(true);
	const [categories, setCategories] = useState([]);
	const [documentsData, setDocumentsData] = useState([]);
	const [selectedRows, setSelectedRows] = useState([]);
	const [isExporting, setIsExporting] = useState(false);
	const [downloadingRowId, setDownloadingRowId] = useState(null);
	const [exportToast, setExportToast] = useState({ open: false, message: "", severity: "success" });
	const [selectedSize, setSelectedSize] = useState(null); // null = no selection, -1 = loading, N = bytes
	const sizeTimerRef = useRef(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedCategoryFilter, setSelectedCategoryFilter] = useState([]);
	const [selectedMachineFilter, setSelectedMachineFilter] = useState([]);
	const [categoryFilterOpen, setCategoryFilterOpen] = useState(false);
	const [machineFilterOpen, setMachineFilterOpen] = useState(false);
	const [machineFilterOptions, setMachineFilterOptions] = useState([]);
	const { selectedSite, siteData, selectedSiteData, loading: siteLoading } = useSite();
	const { machine_id } = useParams();
	const [searchParams] = useSearchParams();
	const selectedSiteLineId = searchParams.get("site_line_id") || "";
	const categoryParam = searchParams.get("category") || "";
	const [selectedMachineId, setSelectedMachineId] = useState(null);
	const [selectedMachine, setSelectedMachine] = useState(null);
	const { t } = useTranslation();
	const isContextualDocumentsView = Boolean(machine_id || selectedSiteLineId || categoryParam);
	const rowSelectedLabel = t("UI_TEXT.ROW_SELECTED");
	const rowsSelectedLabel = t("UI_TEXT.ROWS_SELECTED");
	const selectedRowLabelText = `${selectedRows.length} ${
		selectedRows.length === 1
			? (rowSelectedLabel !== "UI_TEXT.ROW_SELECTED" ? rowSelectedLabel : "row selected")
			: (rowsSelectedLabel !== "UI_TEXT.ROWS_SELECTED" ? rowsSelectedLabel : "rows selected")
	}`;

	const normalizedSearchTerm = searchTerm.trim().toLowerCase();
	const formatDocumentDate = (value) => {
		if (!value) return "-";
		const date = new Date(value);
		if (Number.isNaN(date.getTime())) return "-";
		return date.toLocaleDateString();
	};
	const matchesDocument = (doc) => {
		if (!doc) return false;
		if (!normalizedSearchTerm) return true;
		const docName = (doc.name || "").toLowerCase();
		const categoryNames = (Array.isArray(doc.categories) ? doc.categories : [])
			.map((c) => (c?.category_name || "").toLowerCase())
			.join(" ");
		const description = (doc.description || "").replace(/<[^>]+>/g, "").toLowerCase();
		const assignedMachines = Array.isArray(doc.assigned_machines) ? doc.assigned_machines : [];
		const machineNames = assignedMachines
			.map((m) => (m?.machine_name || "").toLowerCase())
			.join(" ");
		const machineNumbers = assignedMachines
			.map((m) => formatMachineNumberFromRow(m).toLowerCase())
			.join(" ");
		return (
			docName.includes(normalizedSearchTerm) ||
			categoryNames.includes(normalizedSearchTerm) ||
			description.includes(normalizedSearchTerm) ||
			machineNames.includes(normalizedSearchTerm) ||
			machineNumbers.includes(normalizedSearchTerm)
		);
	};

	const isExternalHttpUrl = (value) => {
		try {
			const parsed = new URL(value, window.location.href);
			return parsed.protocol === "http:" || parsed.protocol === "https:";
		} catch (_error) {
			return false;
		}
	};

	const checkAndOpenUrl = async (url) => {
		if (!url) return;
		let parsedUrl;
		try {
			parsedUrl = new URL(url, window.location.href);
		} catch (_error) {
			parsedUrl = null;
		}

		if (parsedUrl && parsedUrl.origin !== window.location.origin && isExternalHttpUrl(parsedUrl.href)) {
			window.open(parsedUrl.href, "_blank", "noopener,noreferrer");
			return;
		}

		try {
			const response = await fetch(url, { method: "HEAD", redirect: "follow" });
			if (!response.ok && !(response.status >= 300 && response.status < 400) && !response.redirected) {
				throw new Error(`Status ${response.status}`);
			}
			window.open(parsedUrl?.href || url, "_blank", "noopener,noreferrer");
		} catch (error) {
			if (parsedUrl && parsedUrl.origin !== window.location.origin && isExternalHttpUrl(parsedUrl.href)) {
				window.open(parsedUrl.href, "_blank", "noopener,noreferrer");
				return;
			}
			console.error("Unable to open document url", error);
			Swal.fire({
				icon: "error",
				title: t('ALERT_MESSAGES.ERROR'),
				text: t('ALERT_MESSAGES.UNABLE_TO_OPEN_DOCUMENT') || "Unable to open the document. Please try again later.",
			});
		}
	};

	// Show loader during initial fetch

	const [viewOpen, setViewOpen] = useState(false);
	const [viewDoc, setViewDoc] = useState(null);
	const navigate = useNavigate();

	useEffect(() => {
		const fetchMachineDetails = async () => {
			try {
				const response = await getMachinesByIds({
					machines: [machine_id],
					site_line_id: selectedSiteLineId,
					site_number: selectedSite,
					site_id: selectedSiteData?.site_id,
					cus_id: selectedSiteData?.cus_id,
				});
				console.log("In documents, getMachines by ids response", response);
				if (response.status && Array.isArray(response.data) && response.data.length > 0) {
					setSelectedMachine(response.data[0]);
				}
			} catch (error) {
				console.log(error);
			}
		};
		if (machine_id) {
			fetchMachineDetails();
			setSelectedMachineId(machine_id);
			setSelectedMachineFilter([String(machine_id)]);
		} else {
			setSelectedMachine(null);
			setSelectedMachineId(null);
			setSelectedMachineFilter([]);
		}
	}, [machine_id, selectedSite, selectedSiteData?.site_id, selectedSiteData?.cus_id, selectedSiteLineId]);

	// Pre-apply the Category filter when arriving from Global Search's
	// "Document Categories" results (navigated to /documents?category=...).
	useEffect(() => {
		if (categoryParam) {
			setSelectedCategoryFilter([categoryParam]);
		} else {
			setSelectedCategoryFilter([]);
		}
	}, [categoryParam]);

	useEffect(() => {
		if (siteLoading) {
			return;
		}
		setLoading(true);
		const fetchDocuments = async () => {
			try {
				const siteId = selectedSiteData?.site_id || siteData.find((i) => i.customer_site_number === selectedSite)?.site_id;
				console.log("In documents, selectedSite and siteId", selectedSite, siteId);
				if (!selectedSite || !siteId) {
					setCategories([]);
					setDocumentsData([]);
					setSelectedCategoryFilter([]);
					setLoading(false);
					return;
				}

				// Fetch Categories
				const catResponse = await getDocumentCategories();
				console.log("In documents, getdoccategories response", catResponse);
				let newCategories = (Array.isArray(catResponse?.data) ? catResponse.data : []).map((category) => ({
					id: category.id,
					name: category.category_name,
				}));

				// Fetch Documents for the whole site so the machine filter can combine
				// multiple machines client-side, regardless of which machine (if any)
				// was used to navigate here.
				const docResponse = await getDocuments(siteId, null, selectedSiteData?.cus_id, selectedSiteLineId);
				console.log("In documents, getdocuments response", docResponse);
				if (docResponse.status) {
					const databaseDocsRaw = docResponse.data;
					const databaseDocs = Array.isArray(databaseDocsRaw) ? databaseDocsRaw : [];
					const safeDocs = databaseDocs.filter((d) => d && typeof d === 'object');

					// Limit to a single machine if routed with machine_id
					const docsFiltered = safeDocs;
					console.log("Docs after filtering:", docsFiltered);
					console.log("Safe docs before filtering:", safeDocs);
					const categoryNamesSeen = new Set();
					const flatDocuments = docsFiltered.filter(Boolean).map((doc) => {
						const categoriesForDoc = Array.isArray(doc.categories) && doc.categories.length > 0
							? doc.categories
							: [{ category_name: t('UI_TEXT.UNCATEGORIZED') || 'Uncategorized' }];
						const type = doc.attachment_type || '';
						let path = '';
						if (type === 'file') path = doc.attachment || `Documents/${doc.name || ''}`;
						else if (type === 'url') path = doc.attachment || '';

						// doc.machine_details is already scoped server-side (attachMachineDetails
						// in document.controller.js) to the site_id/cus_id sent with the request —
						// i.e. exactly the site the user has selected (which for an admin with no
						// specific site allocation can be any site, by design). No further
						// client-side site-membership filtering is needed or possible here, since
						// the selected site's machine list isn't available on the frontend.
						let assignedSiteMachines = Array.isArray(doc?.machine_details) ? doc.machine_details : [];
						const seen = new Set();
						assignedSiteMachines = assignedSiteMachines.filter((m) => {
							const machineKey = String(m?.machine_id);
							if (seen.has(machineKey)) return false;
							seen.add(machineKey);
							return true;
						});

						const normalizedCategories = categoriesForDoc.map((cat) => {
							const name = cat.category_name || t('UI_TEXT.UNCATEGORIZED') || 'Uncategorized';
							categoryNamesSeen.add(name);
							return {
								category_name: name,
								category_id: Number.isFinite(Number(cat.category_id)) ? Number(cat.category_id) : null,
							};
						});

						return {
							id: doc.id,
							name: doc.name || t('UI_TEXT.UNTITLED'),
							document_id: Number(doc.id) || null,
							path,
							attachment_type: type,
							description: doc.description || t('UI_TEXT.NO_DESCRIPTION_AVAILABLE_1'),
							assigned_machines: assignedSiteMachines,
							categories: normalizedCategories,
							attachment: doc.attachment || null,
							visibility_status: doc.visibility_status || 'private',
							version_number: doc.version_number || doc.version || "1.0",
							created_date: doc.created_at || doc.created_date || doc.createdAt || null,
							modified_date: doc.updated_at || doc.modified_at || doc.modified_date || doc.updatedAt || null,
							created_at: doc.created_at || null,
							updated_at: doc.updated_at || null,
						};
					});

					const filteredCategories = newCategories.filter((cat) => categoryNamesSeen.has(cat.name));
					categoryNamesSeen.forEach((name) => {
						if (!filteredCategories.some((cat) => cat.name === name)) {
							filteredCategories.push({ id: null, name });
						}
					});
					console.log("Filtered categories", filteredCategories, "New categories", newCategories);
					setCategories(filteredCategories);
					setDocumentsData(flatDocuments);
					setSelectedRows([]);

					// Build the distinct list of machines referenced by the fetched
					// documents, to populate the multi-select machine filter.
					const machineOptionsMap = new Map();
					flatDocuments.forEach((entry) => {
						(entry.assigned_machines || []).forEach((m) => {
							const key = String(m?.machine_id || "");
							if (!key || machineOptionsMap.has(key)) return;
							machineOptionsMap.set(key, {
								machine_id: key,
								label: formatMachineNumberFromRow(m) || m?.machine_name || key,
							});
						});
					});
					setMachineFilterOptions(
						Array.from(machineOptionsMap.values()).sort((a, b) => a.label.localeCompare(b.label))
					);
				} else {
					console.error("Error fetching documents:", docResponse);
				}
			} catch (err) {
				console.error("Error fetching documents:", err);
			} finally {
				setLoading(false);
			}
		};

		const fetchMachineDetails = async () => {
			try {
				const response = await getMachinesByIds({
					machines: [machine_id],
					site_line_id: selectedSiteLineId,
					site_number: selectedSite,
					site_id: selectedSiteData?.site_id,
					cus_id: selectedSiteData?.cus_id,
				});
				console.log("In documents, getMachines by ids response", response);
				if (response.status && Array.isArray(response.data) && response.data.length > 0) {
					setSelectedMachine(response.data[0]);
				}
			} catch (error) {
				console.log(error);
			}
		};
		if (machine_id) {
			fetchMachineDetails();
			setSelectedMachineId(machine_id);
			fetchDocuments();
		} else {
			fetchDocuments();
		}
	}, [selectedSite, machine_id, siteData, selectedSiteData, selectedSiteLineId, siteLoading]);

	const handleSearchChange = (value) => {
		setSearchTerm(value);
	};

	const formatBytes = (bytes) => {
		if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
		const units = ["B", "KB", "MB", "GB"];
		const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
		return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
	};

	useEffect(() => {
		clearTimeout(sizeTimerRef.current);
		if (selectedRows.length === 0) {
			setSelectedSize(null);
			return;
		}
		setSelectedSize(-1);
		sizeTimerRef.current = setTimeout(async () => {
			try {
				const selectedSet = new Set(selectedRows.map((id) => String(id)));
				const selectedDocs = activeDocuments.filter((row) => selectedSet.has(String(row.row_id)));
				const documentIds = Array.from(
					new Set(
						selectedDocs
							.map((row) => Number(row.document_id))
							.filter((id) => Number.isFinite(id) && id > 0)
					)
				);
				if (documentIds.length === 0) { setSelectedSize(0); return; }
				const siteId = selectedSiteData?.site_id || siteData.find((i) => i.customer_site_number === selectedSite)?.site_id;
				const result = await getDocumentsSizeEstimate({
					document_ids: documentIds,
					site_id: siteId,
					...(selectedSiteData?.cus_id ? { cus_id: selectedSiteData.cus_id } : {}),
				});
				setSelectedSize(result?.data?.totalBytes ?? 0);
			} catch (_err) {
				setSelectedSize(0);
			}
		}, 600);
		return () => clearTimeout(sizeTimerRef.current);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedRows]);

	const flatListDocuments = useMemo(() => {
		return documentsData
			.filter((doc) => matchesDocument(doc))
			.filter((doc) => {
				if (selectedCategoryFilter.length === 0) return true;
				return (Array.isArray(doc.categories) ? doc.categories : []).some((c) =>
					selectedCategoryFilter.includes(c.category_name)
				);
			})
			.filter((doc) => {
				if (selectedMachineFilter.length === 0) return true;
				return (Array.isArray(doc.assigned_machines) ? doc.assigned_machines : []).some((m) =>
					selectedMachineFilter.includes(String(m?.machine_id))
				);
			})
			.map((doc) => {
				// When a filter is active, only show/export the categories or machines
				// that matched it — not the document's full set — so a document with 3
				// machines filtered down to 1 displays and exports just that 1.
				const displayedCategories =
					selectedCategoryFilter.length > 0
						? (doc.categories || []).filter((c) => selectedCategoryFilter.includes(c.category_name))
						: doc.categories || [];
				const displayedMachines =
					selectedMachineFilter.length > 0
						? (doc.assigned_machines || []).filter((m) => selectedMachineFilter.includes(String(m?.machine_id)))
						: doc.assigned_machines || [];
				return {
					...doc,
					row_id: doc.id || doc.name,
					document_id: Number(doc.document_id) || null,
					displayed_categories: displayedCategories,
					displayed_machines: displayedMachines,
				};
			});
	}, [documentsData, normalizedSearchTerm, selectedCategoryFilter, selectedMachineFilter]);

	const activeDocuments = flatListDocuments;

	const handleBulkExport = async () => {
		if (!selectedRows || selectedRows.length === 0) return;
		setIsExporting(true);
		setExportToast({
			open: true,
			message: t("ALERT_MESSAGES.DOCUMENT_EXPORT_IN_PROGRESS") !== "ALERT_MESSAGES.DOCUMENT_EXPORT_IN_PROGRESS"
				? t("ALERT_MESSAGES.DOCUMENT_EXPORT_IN_PROGRESS")
				: "Exporting selected documents. Please wait...",
			severity: "success",
		});
		try {
			const selectedSet = new Set(selectedRows.map((id) => String(id)));
			const selectedDocs = activeDocuments.filter((row) => selectedSet.has(String(row.row_id)));
			const documentIds = Array.from(
				new Set(
					selectedDocs
						.map((row) => Number(row.document_id))
						.filter((id) => Number.isFinite(id) && id > 0)
				)
			);
			if (documentIds.length === 0) {
				Swal.fire({
					icon: "warning",
					title: t("ALERT_MESSAGES.WARNING") || "Warning",
					text: t("ALERT_MESSAGES.SELECT_AT_LEAST_ONE_DOCUMENT") || "Select at least one document to export.",
				});
				return;
			}

			// Each selected row carries only the categories/machines that matched the
			// currently applied filters (displayed_categories/displayed_machines) — send
			// those pairings along so the export only creates folders for what the user
			// actually filtered to, instead of every category/machine the document belongs to.
			const documentCategoryPairs = [];
			const documentMachinePairs = [];
			selectedDocs.forEach((row) => {
				const docId = Number(row.document_id);
				if (!Number.isFinite(docId) || docId <= 0) return;

				const categoriesForRow = Array.isArray(row.displayed_categories) && row.displayed_categories.length > 0
					? row.displayed_categories
					: row.categories;
				(Array.isArray(categoriesForRow) ? categoriesForRow : []).forEach((cat) => {
					documentCategoryPairs.push({
						document_id: docId,
						category_id: Number.isFinite(Number(cat?.category_id)) ? Number(cat.category_id) : null,
						category_name: cat?.category_name || null,
					});
				});

				const machinesForRow = Array.isArray(row.displayed_machines) && row.displayed_machines.length > 0
					? row.displayed_machines
					: row.assigned_machines;
				(Array.isArray(machinesForRow) ? machinesForRow : []).forEach((machine) => {
					if (!machine?.machine_id) return;
					documentMachinePairs.push({
						document_id: docId,
						machine_id: Number(machine.machine_id),
						machine_number: machine?.machine_number || null,
					});
				});
			});

			const siteId = selectedSiteData?.site_id || siteData.find((i) => i.customer_site_number === selectedSite)?.site_id;
			const payload = {
				document_ids: documentIds,
				document_category_pairs: documentCategoryPairs,
				document_machine_pairs: documentMachinePairs,
				site_id: siteId,
				...(selectedSiteData?.cus_id ? { cus_id: selectedSiteData.cus_id } : {}),
			};

				const response = await exportDocumentsBulk(payload);
				if (response?.status === false || response?.status === "false") {
					throw new Error(response?.message || response?.error_message || "Failed to export documents");
				}
				if (!response || typeof response !== "object" || !("data" in response)) {
					throw new Error(response?.message || response?.error_message || "Failed to export documents");
				}
				const blob = response?.data;
				const contentType = String(response?.headers?.["content-type"] || "");
				const isZipResponse =
					contentType.includes("application/zip") ||
					contentType.includes("application/octet-stream");
			if (!(blob instanceof Blob)) throw new Error("Invalid export response");
			if (!isZipResponse) {
				let parsedMessage = "";
				try {
					const text = await blob.text();
					if (text) {
						try {
							const json = JSON.parse(text);
							parsedMessage = json?.message || json?.error_message || "";
						} catch (_jsonErr) {
							parsedMessage = text;
						}
					}
				} catch (_parseErr) {
					// no-op
				}
				throw new Error(parsedMessage || "Invalid export response");
			}

			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `documents-export-${Date.now()}.zip`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
			setExportToast({
				open: true,
				message: t("ALERT_MESSAGES.DOCUMENT_EXPORT_SUCCESS") !== "ALERT_MESSAGES.DOCUMENT_EXPORT_SUCCESS"
					? t("ALERT_MESSAGES.DOCUMENT_EXPORT_SUCCESS")
					: "Documents exported successfully. Your download has started.",
				severity: "success",
			});
		} catch (error) {
			setExportToast({ open: false, message: "", severity: "success" });
			let errorMessage = error?.message || "Failed to export documents";
			try {
				const blobData = error?.response?.data;
				if (blobData instanceof Blob) {
					const text = await blobData.text();
					if (text) {
						try {
							const json = JSON.parse(text);
							errorMessage = json?.message || json?.error_message || text || errorMessage;
						} catch (_jsonErr) {
							errorMessage = text;
						}
					}
				} else if (blobData?.message) {
					errorMessage = blobData.message;
				}
			} catch (_parseErr) {
				// keep fallback
			}
			Swal.fire({
				icon: "error",
				title: t("ALERT_MESSAGES.ERROR"),
				text: errorMessage,
			});
		} finally {
			setIsExporting(false);
		}
	};

	const lightTooltipSlotProps = {
		tooltip: {
			sx: {
				bgcolor: "#535353",
				color: "#ffffff",
				fontSize: 12,
				fontWeight: 600,
				borderRadius: 1.1,
				boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
				px: 1.5,
				py: 0.75,
				maxWidth: "none",
				whiteSpace: "nowrap",
			},
		},
	};

	// Description text runs long, so unlike the compact tooltip above, this
	// one keeps wrapping and a max width instead of a single nowrap line.
	const lightWrappingTooltipSlotProps = {
		tooltip: {
			sx: {
				bgcolor: "#535353",
				color: "#ffffff",
				fontSize: 12,
				fontWeight: 600,
				borderRadius: 1.1,
				boxShadow: "0px 2px 8px rgba(0,0,0,0.15)",
				px: 1.25,
				py: 0.75,
				maxWidth: 320,
			},
		},
	};

	const renderTruncatedCellWithTooltip = (value) => {
		const text = value || "-";
		return (
			<Tooltip title={text} slotProps={lightTooltipSlotProps}>
				<Box
					sx={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						width: "100%",
					}}
				>
					{text}
				</Box>
			</Tooltip>
		);
	};

	const columns = [
		{
			field: "name",
			headerName: t('TABLE_HEADERS.DOCUMENT_NAME'),
			flex: 1,
			renderCell: (params) => renderTruncatedCellWithTooltip(params.value),
		},
		{
			field: "category_name",
			headerName: t('TABLE_HEADERS.CATEGORY') || "Category",
			flex: 1,
			valueGetter: (_value, row) => {
				const cats = row?.displayed_categories || row?.categories;
				const names = (Array.isArray(cats) ? cats : [])
					.map((c) => c?.category_name)
					.filter(Boolean)
					.join(", ");
				return names || "-";
			},
			renderCell: (params) => renderTruncatedCellWithTooltip(params.value),
		},
		{
			field: "description",
			headerName: t('TABLE_HEADERS.DESCRIPTION'),
			flex: 2,
			renderCell: (params) => {
				// Remove HTML tags and trim whitespace
				const plainText = (params.value || '')
					.replace(/<[^>]+>/g, '')
					.trim();

				return (
					<Tooltip title={plainText || ''} slotProps={lightWrappingTooltipSlotProps}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								height: '100%',
								width: '100%',
								overflow: 'hidden',
							}}
						>
							<Box
								sx={{
									display: '-webkit-box',
									WebkitLineClamp: 2,
									WebkitBoxOrient: 'vertical',
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									lineHeight: '1.3em',
									maxHeight: '2.6em',
									width: '100%',
									color:
										plainText === t('UI_TEXT.NO_DESCRIPTION_AVAILABLE_1')
											? 'text.secondary'
											: 'inherit',
									wordBreak: 'break-word',
								}}
							>
								{plainText || ''}
							</Box>
						</Box>
					</Tooltip>
				);
			},
			cellClassName: (params) => {
				const plainText = (params.value || '').replace(/<[^>]+>/g, '').trim();
				return plainText === t('UI_TEXT.NO_DESCRIPTION_AVAILABLE_1') ? "gray-text" : "";
			},
		},
		{
			field: "assigned_machines",
			headerName: t('TABLE_HEADERS.ASSIGNED_MACHINE'),
			flex: 2,
			valueGetter: (_value, row) => {
				const assignedMachines = row?.displayed_machines || row?.assigned_machines;
				if (Array.isArray(assignedMachines) && assignedMachines.length > 0) {
					const names = assignedMachines
						.map((m) => m?.machine_name || formatMachineNumberFromRow(m) || m?.machine_id)
						.filter(Boolean)
						.join(", ");
					return names || t('UI_TEXT.NO_MACHINES_ASSIGNED_1');
				}
				return t('UI_TEXT.NO_MACHINES_ASSIGNED_1');
			},
			renderCell: (params) => renderTruncatedCellWithTooltip(params.value),
			cellClassName: (params) => {
				return params.value === t('UI_TEXT.NO_MACHINES_ASSIGNED_1') ? "gray-text" : "";
			},
		},
		{
			field: "machine_number",
			headerName: t('TABLE_HEADERS.MACHINE_NUMBER') || "Machine Number",
			flex: 2,
			valueGetter: (_value, row) => {
				const assignedMachines = row?.displayed_machines || row?.assigned_machines;
				if (Array.isArray(assignedMachines) && assignedMachines.length > 0) {
					const numbers = assignedMachines
						.map((m) => formatMachineNumberFromRow(m))
						.filter(Boolean)
						.join(", ");
					return numbers || t('UI_TEXT.NO_MACHINES_ASSIGNED_1');
				}
				return t('UI_TEXT.NO_MACHINES_ASSIGNED_1');
			},
			renderCell: (params) => renderTruncatedCellWithTooltip(params.value),
			cellClassName: (params) => {
				return params.value === t('UI_TEXT.NO_MACHINES_ASSIGNED_1') ? "gray-text" : "";
			},
		},
		{
			field: "actions",
			headerName: t('TABLE_HEADERS.ACTIONS'),
			width: 140,
			headerAlign: 'center',
			align: 'center',
			renderCell: (params) => {
				const { row } = params;
				const hasUrlAttachment = row?.attachment_type === 'url' && row?.attachment;
				const hasFileAttachment = row?.attachment_type === 'file' && row?.attachment;

				const handleViewDocument = (event) => {
					event.stopPropagation();
					setViewDoc(row);
					setViewOpen(true);
				};

				const handleOpenUrl = (event) => {
					event.stopPropagation();
					checkAndOpenUrl(row?.attachment);
				};

				const handleDownload = async (event) => {
					event.stopPropagation();
					const filename = row.path.split("/").pop();
					setDownloadingRowId(row.id);
					try {
						const response = await getDocumentAccessUrl(encodeURIComponent(filename), "download");
						const signedUrl = response?.data?.url;
						if (!signedUrl) {
							throw new Error("Missing signed download url");
						}
						const link = document.createElement("a");
						link.href = signedUrl;
						link.download = filename;
						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);
					} catch (error) {
						Swal.fire({
							icon: "error",
							title: t("ALERT_MESSAGES.ERROR"),
							text: t("ALERT_MESSAGES.FAILED_TO_DOWNLOAD_FILE_PLEASE_TRY_AGAING"),
						});
					} finally {
						setDownloadingRowId(null);
					}
				};

				return (
					<Box sx={{ display: "flex", flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', height: '100%' }}>

						<Box sx={{ display: "flex", justifyContent: 'flex-start', alignItems: 'center' }}>
							<Tooltip title={t("UI_TEXT.TOOLTIP_VIEW_DOCUMENT")}>
								<IconButton color="primary" onClick={handleViewDocument}>
									<VisibilityIcon fontSize="small" />
								</IconButton>
							</Tooltip>
							{hasUrlAttachment && (
								<Tooltip title={t("UI_TEXT.TOOLTIP_OPEN_URL")}>
									<IconButton color="secondary" onClick={handleOpenUrl}>
										<LaunchIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							)}
							{hasFileAttachment && (
								<Tooltip title={t("UI_TEXT.TOOLTIP_DOWNLOAD_FILE")}>
									<span>
										<IconButton
											color="secondary"
											onClick={handleDownload}
											disabled={downloadingRowId === row.id}
										>
											{downloadingRowId === row.id ? (
												<CircularProgress size={18} color="inherit" />
											) : (
												<DownloadIcon fontSize="small" />
											)}
										</IconButton>
									</span>
								</Tooltip>
							)}
						</Box>
					</Box>
				);
			},
		},
	];

	columns.splice(columns.length - 1, 0,
		{
			field: "version_number",
			headerName: t('TABLE_HEADERS.VERSION_NUMBER') || "Version Number",
			width: 150,
			valueGetter: (value, row) => row?.version_number ?? value ?? "1",
			renderCell: (params) => renderTruncatedCellWithTooltip(params.value),
		},
		{
			field: "created_date",
			headerName: t('TABLE_HEADERS.CREATED_DATE') || "Created Date",
			width: 150,
			valueGetter: (value, row) => formatDocumentDate(
				row?.created_date ||
				row?.created_at ||
				row?.createdAt ||
				value
			),
			renderCell: (params) => renderTruncatedCellWithTooltip(params.value),
		},
		{
			field: "modified_date",
			headerName: t('TABLE_HEADERS.MODIFIED_DATE') || "Modified Date",
			width: 150,
			valueGetter: (value, row) => formatDocumentDate(
				row?.modified_date ||
				row?.updated_at ||
				row?.modified_at ||
				row?.updatedAt ||
				value
			),
			renderCell: (params) => renderTruncatedCellWithTooltip(params.value),
		},
	);

	const hasActiveDocumentFilters = useMemo(() => {
		const isAllCategories =
			selectedCategoryFilter.length === 0 ||
			(categories.length > 0 && selectedCategoryFilter.length === categories.length);
		const isAllMachines =
			selectedMachineFilter.length === 0 ||
			(machineFilterOptions.length > 0 && selectedMachineFilter.length === machineFilterOptions.length);
		return !isAllCategories || !isAllMachines;
	}, [selectedCategoryFilter, selectedMachineFilter, categories, machineFilterOptions]);

	const handleClearAllDocumentFilters = () => {
		setSelectedCategoryFilter([]);
		setSelectedMachineFilter([]);
		setSelectedRows([]);
	};

	const handleShowAllDocuments = () => {
		setSelectedCategoryFilter([]);
		setSelectedMachineFilter([]);
		setSearchTerm("");
		setSelectedRows([]);
		navigate("/documents");
	};

	if (loading) {
		return <ListPageSkeleton showAction={false} />;
	}

	return (
		<Box
			sx={{
				padding: "5px 40px 40px 40px",
				display: "flex",
				flexDirection: "column",
				justifyContent: "flex-start",
				width: "100%",
				minHeight: "500px",
				height: '100%',
			}}>
			<PageTitle
				title={t('UI_TEXT.DOCUMENT') || "Documents"}
			/>

			<Box
				sx={{
					display: "flex",
					width: "100%",
					marginTop: "20px",
					height: "100%",
					minHeight: '500px'
				}}>
				<Box
					sx={{
						width: "100%",
					}}
				>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: { xs: "stretch", md: "center" },
								flexDirection: { xs: "column", md: "row" },
								mb: 2,
								gap: 1.5,
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1.5,
									flexWrap: "wrap",
									flex: 1,
								}}
							>
								<Box sx={{ width: { xs: "100%", sm: 320 } }}>
									<ContextFilterAndSort
										showSearch
										showSort={false}
										searchValue={searchTerm}
										onSearchChange={handleSearchChange}
									/>
								</Box>
								<FormControl size="small" sx={{ width: { xs: "100%", sm: 320 } }}>
									<InputLabel id="document-category-filter-label">
										{t("TABLE_HEADERS.CATEGORY") || "Category"}
									</InputLabel>
									<Select
										multiple
										labelId="document-category-filter-label"
										value={selectedCategoryFilter}
										label={t("TABLE_HEADERS.CATEGORY") || "Category"}
										open={categoryFilterOpen}
										onOpen={() => setCategoryFilterOpen(true)}
										onClose={() => setCategoryFilterOpen(false)}
										IconComponent={() => null}
										sx={{
											height: 40,
											width: "100%",
											"& .MuiSelect-select": {
												display: "flex",
												alignItems: "center",
												minWidth: 0,
												width: "100%",
												boxSizing: "border-box",
												paddingRight: "76px !important",
											},
										}}
										onChange={(event) => {
											const { value } = event.target;
											const values = typeof value === "string" ? value.split(",") : value;
											if (values.includes("__all__")) {
												setSelectedCategoryFilter(
													selectedCategoryFilter.length === categories.length
														? []
														: categories.map((c) => c.name)
												);
											} else {
												setSelectedCategoryFilter(values);
											}
											setSelectedRows([]);
										}}
										renderValue={(selected) => {
											if (selected.length === 0 || (selected.length === categories.length && categories.length > 0)) {
												return <Chip label={t("MENU_ITEMS.ALL") || "All"} size="small" />;
											}
											return (
												<Box
													sx={{
														display: "flex",
														flexWrap: "nowrap",
														gap: 0.5,
														alignItems: "center",
														overflowX: "auto",
														overflowY: "hidden",
														minWidth: 0,
														width: "100%",
														"&::-webkit-scrollbar": { height: 6 },
													}}
												>
													{selected.map((val) => (
														<Chip
															key={val}
															label={val}
															size="small"
															sx={{ flexShrink: 0, maxWidth: 160 }}
															onDelete={() => {
																setSelectedCategoryFilter((prev) => prev.filter((v) => v !== val));
																setSelectedRows([]);
															}}
															deleteIcon={
																<ClearIcon
																	fontSize="small"
																	onMouseDown={(event) => event.stopPropagation()}
																/>
															}
														/>
													))}
												</Box>
											);
										}}
										endAdornment={
											<Box
												sx={{
													position: "absolute",
													right: 4,
													display: "flex",
													alignItems: "center",
												}}
											>
												{selectedCategoryFilter.length > 0 ? (
													<IconButton
														size="small"
														onMouseDown={(event) => event.stopPropagation()}
														onClick={(event) => {
															event.stopPropagation();
															setSelectedCategoryFilter([]);
															setSelectedRows([]);
														}}
													>
														<ClearIcon fontSize="small" />
													</IconButton>
												) : null}
												<Tooltip title={categoryFilterOpen ? t("UI_TEXT.TOOLTIP_CLOSE") : t("UI_TEXT.TOOLTIP_OPEN")}>
													<IconButton
														size="small"
														onMouseDown={(event) => event.stopPropagation()}
														onClick={(event) => {
															event.stopPropagation();
															setCategoryFilterOpen((prev) => !prev);
														}}
													>
														<ArrowDropDownIcon
															fontSize="small"
															sx={{
																transition: "transform 0.15s ease-in-out",
																transform: categoryFilterOpen ? "rotate(180deg)" : "rotate(0deg)",
															}}
														/>
													</IconButton>
												</Tooltip>
											</Box>
										}
										MenuProps={{
											PaperProps: { style: { maxHeight: 300, width: 320 } },
										}}
									>
										<MenuItem value="__all__">
											<ListItemText primary={t("MENU_ITEMS.ALL") || "All"} />
										</MenuItem>
										<Divider />
										{categories.map((category) => (
											<MenuItem
												key={category.name}
												value={category.name}
												sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
											>
												<ListItemText primary={category.name} />
											</MenuItem>
										))}
									</Select>
								</FormControl>
								<FormControl size="small" sx={{ width: { xs: "100%", sm: 420 } }}>
									<InputLabel id="document-machine-filter-label">
										{t("TABLE_HEADERS.MACHINE_NUMBER") || "Machine Number"}
									</InputLabel>
									<Select
										multiple
										labelId="document-machine-filter-label"
										value={selectedMachineFilter}
										label={t("TABLE_HEADERS.MACHINE_NUMBER") || "Machine Number"}
										open={machineFilterOpen}
										onOpen={() => setMachineFilterOpen(true)}
										onClose={() => setMachineFilterOpen(false)}
										IconComponent={() => null}
										sx={{
											height: 40,
											width: "100%",
											"& .MuiSelect-select": {
												display: "flex",
												alignItems: "center",
												minWidth: 0,
												width: "100%",
												boxSizing: "border-box",
												paddingRight: "76px !important",
											},
										}}
										onChange={(event) => {
											const { value } = event.target;
											const values = typeof value === "string" ? value.split(",") : value;
											if (values.includes("__all__")) {
												setSelectedMachineFilter(
													selectedMachineFilter.length === machineFilterOptions.length
														? []
														: machineFilterOptions.map((m) => m.machine_id)
												);
											} else {
												setSelectedMachineFilter(values);
											}
											setSelectedRows([]);
										}}
										renderValue={(selected) => {
											if (selected.length === 0 || (selected.length === machineFilterOptions.length && machineFilterOptions.length > 0)) {
												return <Chip label={t("MENU_ITEMS.ALL") || "All"} size="small" />;
											}
											const labelById = new Map(machineFilterOptions.map((m) => [m.machine_id, m.label]));
											return (
												<Box
													sx={{
														display: "flex",
														flexWrap: "nowrap",
														gap: 0.5,
														alignItems: "center",
														overflowX: "auto",
														overflowY: "hidden",
														minWidth: 0,
														width: "100%",
														"&::-webkit-scrollbar": { height: 6 },
													}}
												>
													{selected.map((val) => (
														<Chip
															key={val}
															label={labelById.get(val) || val}
															size="small"
															sx={{ flexShrink: 0, maxWidth: 180 }}
															onDelete={() => {
																setSelectedMachineFilter((prev) => prev.filter((v) => v !== val));
																setSelectedRows([]);
															}}
															deleteIcon={
																<ClearIcon
																	fontSize="small"
																	onMouseDown={(event) => event.stopPropagation()}
																/>
															}
														/>
													))}
												</Box>
											);
										}}
										endAdornment={
											<Box
												sx={{
													position: "absolute",
													right: 4,
													display: "flex",
													alignItems: "center",
												}}
											>
												{selectedMachineFilter.length > 0 ? (
													<IconButton
														size="small"
														onMouseDown={(event) => event.stopPropagation()}
														onClick={(event) => {
															event.stopPropagation();
															setSelectedMachineFilter([]);
															setSelectedRows([]);
														}}
													>
														<ClearIcon fontSize="small" />
													</IconButton>
												) : null}
												<Tooltip title={machineFilterOpen ? t("UI_TEXT.TOOLTIP_CLOSE") : t("UI_TEXT.TOOLTIP_OPEN")}>
													<IconButton
														size="small"
														onMouseDown={(event) => event.stopPropagation()}
														onClick={(event) => {
															event.stopPropagation();
															setMachineFilterOpen((prev) => !prev);
														}}
													>
														<ArrowDropDownIcon
															fontSize="small"
															sx={{
																transition: "transform 0.15s ease-in-out",
																transform: machineFilterOpen ? "rotate(180deg)" : "rotate(0deg)",
															}}
														/>
													</IconButton>
												</Tooltip>
											</Box>
										}
										MenuProps={{
											PaperProps: { style: { maxHeight: 300, width: 420 } },
										}}
									>
										<MenuItem value="__all__">
											<ListItemText primary={t("MENU_ITEMS.ALL") || "All"} />
										</MenuItem>
										<Divider />
										{machineFilterOptions.map((machine) => (
											<MenuItem
												key={machine.machine_id}
												value={machine.machine_id}
												sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
											>
												<ListItemText primary={machine.label} />
											</MenuItem>
										))}
									</Select>
								</FormControl>
								{hasActiveDocumentFilters && (
									<Button
										type="button"
										variant="outlined"
										size="small"
										endIcon={<FilterAltOffIcon />}
										onClick={handleClearAllDocumentFilters}
										sx={{
											height: 40,
											minWidth: { xs: "100%", sm: 108 },
											alignSelf: { xs: "stretch", sm: "center" },
											textTransform: "none",
											fontWeight: 700,
										}}
									>
										{t("BUTTON.CLEAR") || "Clear"}
									</Button>
								)}
							</Box>
							<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
								{activeDocuments.length > 0 && (
								<Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
										<Tooltip
											title={t("UI_TEXT.TOOLTIP_EXPORT_DOCUMENTS_INFO")}
											arrow
											placement="top"
										>
											<IconButton size="small" color="primary" sx={{ mr: 0.5 }}>
												<InfoOutlinedIcon sx={{ fontSize: 22 }} />
											</IconButton>
										</Tooltip>
										<Button
											variant="contained"
											startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
											disabled={!selectedRows || selectedRows.length === 0 || isExporting}
											onClick={handleBulkExport}
										>
											{isExporting
												? (t("BUTTON.EXPORTING") !== "BUTTON.EXPORTING" ? t("BUTTON.EXPORTING") : "Exporting...")
												: (t("BUTTON.EXPORT") || "Export")}
										</Button>
									</Box>
								</Box>
							)}
								{isContextualDocumentsView && (
									<Button
										variant="outlined"
										onClick={handleShowAllDocuments}
									>
										{t("BUTTON.SHOW_ALL_DOCUMENTS") || "Show All Documents"}
									</Button>
								)}
							</Box>
						</Box>
						{selectedSize !== null && (
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", marginBottom: 1 }}>
								<Typography variant="body1" fontWeight={600} color={selectedSize === -1 ? "text.disabled" : "text.secondary"}>
									{selectedSize === -1 ? "Calculating size..." : `Total: ${formatBytes(selectedSize)}`}
								</Typography>
							</Box>
					)}

					{activeDocuments.length === 0 ? (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								height: "80%",
								width: "100%",
							}}>
							<Alert severity="info">
								{selectedMachineId && selectedMachine
									? t('ALERT_MESSAGES.NO_DOCUMENTS_ASSIGENED')
									: t('ALERT_MESSAGES.NO_DOCUMENT_IN_CATEGORY')}
							</Alert>
						</Box>
					) : (
						<Box
							sx={{
								display: "flex",
								flexWrap: "wrap",
								gap: "16px",
								justifyContent: "flex-start",
							}}>
								<Datagrid
									data={activeDocuments}
									columns={columns}
									rowIdField="row_id"
									pageSize={50}
									showSearchBar={false}
									rowClick={true}
									checkboxSelection={true}
									checkboxSelectionVisibleOnly={false}
									rowSelectionModel={selectedRows}
									keepNonExistentSelected={true}
									hideFooterSelectedRowCount={false}
									footerSelectedRowText={selectedRows.length > 0 ? selectedRowLabelText : ""}
									onSelect={(ids) => setSelectedRows(ids)}
									onRowClick={(params) => {
										setViewDoc(params.row);
										setViewOpen(true);
									}}
							/>
						</Box>
					)}
				</Box>
			</Box>

				<DocumentViewModal
					open={viewOpen}
					setOpen={setViewOpen}
					document={viewDoc}
				onClose={() => {
					setViewDoc(null);
					setViewOpen(false);
					}}
				/>
				<Snackbar
					open={exportToast.open}
					autoHideDuration={3000}
					onClose={() => setExportToast({ open: false, message: "", severity: "success" })}
					anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				>
					<Alert
						onClose={() => setExportToast({ open: false, message: "", severity: "success" })}
						severity={exportToast.severity}
						variant="filled"
						sx={{ width: "100%" }}
					>
						{exportToast.message}
					</Alert>
				</Snackbar>
			</Box>
		);
	};

export default Documents;
